import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Plus, X, Receipt, Wallet, AlertCircle, Ban, Loader2, Download, PackageCheck, ExternalLink } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime, formatCurrency, formatDate } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

// Accounts Payable (Phase 12.3). List supplier invoices, filter by
// status + supplier + aging bucket, record payments, watch the aging
// summary at the top. Aging status = days_past_due against due_date;
// the nightly cron flips unpaid/partial → overdue for filter clarity.

interface Supplier { id: string; name: string; active: number }
interface PurchaseOrder { id: string; po_number: string; supplier_id: string; status: string }

interface Invoice {
  id: string
  invoice_number: string
  supplier_id: string
  supplier_name: string | null
  purchase_order_id: string | null
  po_number: string | null
  invoice_date: string
  due_date: string
  subtotal_fils: number
  tax_fils: number
  total_fils: number
  amount_paid_fils: number
  status: 'unpaid' | 'partial' | 'paid' | 'overdue' | 'cancelled'
  notes: string | null
  created_at: string
  days_past_due?: number
}

interface Payment {
  id: string
  supplier_invoice_id: string
  payment_date: string
  amount_fils: number
  method: 'cash' | 'bank_transfer' | 'cheque' | 'card' | 'cliq' | 'other'
  reference: string | null
  notes: string | null
  created_at: string
}

interface Aging {
  not_due_fils: number
  bucket_0_30_fils: number
  bucket_31_60_fils: number
  bucket_61_90_fils: number
  bucket_90_plus_fils: number
  total_outstanding_fils: number
  open_invoices: number
}

const STATUS_LABEL: Record<Invoice['status'], string> = {
  unpaid: 'Unpaid', partial: 'Partial', paid: 'Paid', overdue: 'Overdue', cancelled: 'Cancelled',
}
const STATUS_COLOR: Record<Invoice['status'], string> = {
  unpaid:    'bg-gray-500/15 text-gray-400',
  partial:   'bg-amber-500/15 text-amber-400',
  paid:      'bg-green-500/15 text-green-400',
  overdue:   'bg-red-500/15 text-red-400',
  cancelled: 'bg-red-500/15 text-red-400',
}

const METHOD_LABEL: Record<Payment['method'], string> = {
  cash: 'Cash', bank_transfer: 'Bank transfer', cheque: 'Cheque',
  card: 'Card', cliq: 'CliQ', other: 'Other',
}

export default function AccountsPayable() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations', 'accounting'])
  const [filters, setFilters] = useState<{ status: string; supplier_id: string; aging: string }>({ status: '', supplier_id: '', aging: '' })
  const [createOpen, setCreateOpen] = useState(false)
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)

  const query = useMemo(() => {
    const q = new URLSearchParams()
    if (filters.status) q.set('status', filters.status)
    if (filters.supplier_id) q.set('supplier_id', filters.supplier_id)
    if (filters.aging) q.set('aging', filters.aging)
    return q.toString()
  }, [filters])

  const { data, isLoading } = useQuery<{ supplier_invoices: Invoice[]; total: number }>({
    queryKey: ['supplier-invoices', query],
    queryFn: () => api.get(`/supplier-invoices${query ? `?${query}` : ''}`).then(r => r.data),
  })
  const { data: agingData } = useQuery<Aging>({
    queryKey: ['supplier-invoices-aging'],
    queryFn: () => api.get('/supplier-invoices/aging').then(r => r.data),
  })
  const { data: suppliersData } = useQuery<{ suppliers: Supplier[] }>({
    queryKey: ['suppliers'],
    queryFn: () => api.get('/suppliers').then(r => r.data),
  })
  const suppliers = suppliersData?.suppliers ?? []

  function exportCsv() {
    const rows = data?.supplier_invoices ?? []
    if (rows.length === 0) return
    const header = ['Invoice #', 'Supplier', 'PO #', 'Invoice date', 'Due date', 'Days past due', 'Subtotal (JD)', 'Tax (JD)', 'Total (JD)', 'Paid (JD)', 'Outstanding (JD)', 'Status']
    const body = rows.map(r => [
      r.invoice_number, r.supplier_name ?? '', r.po_number ?? '',
      r.invoice_date, r.due_date, r.days_past_due ?? '',
      (r.subtotal_fils / 1000).toFixed(3),
      (r.tax_fils / 1000).toFixed(3),
      (r.total_fils / 1000).toFixed(3),
      (r.amount_paid_fils / 1000).toFixed(3),
      ((r.total_fils - r.amount_paid_fils) / 1000).toFixed(3),
      r.status,
    ])
    const csv = [header, ...body].map(r => r.map(c => {
      const s = String(c)
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s
    }).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `accounts-payable-${new Date().toISOString().slice(0,10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Receipt size={20} className="text-[#c41e3a]" />
            Accounts Payable
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Every supplier invoice + payment. Aging bucket sums up top, filterable table below. A nightly cron flips overdue rows for filter clarity — payments write to <code>supplier_payments</code> and bump the running <code>amount_paid_fils</code> counter atomically.
          </p>
        </div>
        {canManage && (
          <button onClick={() => setCreateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]">
            <Plus size={16} />
            New invoice
          </button>
        )}
      </div>

      {/* Aging summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <AgingTile label="Not due" value={agingData?.not_due_fils ?? 0} tone="neutral" />
        <AgingTile label="0–30 days" value={agingData?.bucket_0_30_fils ?? 0} tone="amber" />
        <AgingTile label="31–60 days" value={agingData?.bucket_31_60_fils ?? 0} tone="red" />
        <AgingTile label="61–90 days" value={agingData?.bucket_61_90_fils ?? 0} tone="red" />
        <AgingTile label="90+ days" value={agingData?.bucket_90_plus_fils ?? 0} tone="red" />
        <AgingTile label="Total outstanding" value={agingData?.total_outstanding_fils ?? 0} tone="bold" sublabel={`${agingData?.open_invoices ?? 0} open`} />
      </div>

      {/* Filters */}
      <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <Field label="Status">
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))} className={selectCls}>
            <option value="">All</option>
            <option value="unpaid">Unpaid</option>
            <option value="partial">Partial</option>
            <option value="overdue">Overdue</option>
            <option value="paid">Paid</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </Field>
        <Field label="Supplier">
          <select value={filters.supplier_id} onChange={e => setFilters(f => ({ ...f, supplier_id: e.target.value }))} className={selectCls}>
            <option value="">All</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </Field>
        <Field label="Aging bucket">
          <select value={filters.aging} onChange={e => setFilters(f => ({ ...f, aging: e.target.value }))} className={selectCls}>
            <option value="">All open</option>
            <option value="not_due">Not due</option>
            <option value="0-30">0–30 days</option>
            <option value="31-60">31–60 days</option>
            <option value="61-90">61–90 days</option>
            <option value="90+">90+ days</option>
          </select>
        </Field>
        <div className="flex items-end">
          <button onClick={exportCsv} className="flex items-center gap-2 text-xs px-3 py-2 rounded-md border border-[#3a3a3a] text-gray-300 hover:bg-white/5">
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.supplier_invoices ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No invoices match the current filters.</div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Invoice #</th>
                <th className="text-left px-5 py-3 font-medium">Supplier</th>
                <th className="text-left px-5 py-3 font-medium">PO</th>
                <th className="text-left px-5 py-3 font-medium">Due date</th>
                <th className="text-right px-5 py-3 font-medium">Total</th>
                <th className="text-right px-5 py-3 font-medium">Paid</th>
                <th className="text-right px-5 py-3 font-medium">Outstanding</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.supplier_invoices ?? []).map(inv => {
                const outstanding = inv.total_fils - inv.amount_paid_fils
                return (
                  <tr key={inv.id}
                      className="border-t border-white/10 hover:bg-white/5 cursor-pointer"
                      onClick={() => setOpenInvoice(inv)}>
                    <td className="px-5 py-3 text-sm text-white font-mono font-semibold">{inv.invoice_number}</td>
                    <td className="px-5 py-3 text-sm text-gray-300">{inv.supplier_name ?? <span className="text-gray-500">—</span>}</td>
                    <td className="px-5 py-3 text-xs text-gray-400 font-mono">{inv.po_number ?? <span className="text-gray-600">—</span>}</td>
                    <td className="px-5 py-3 text-sm text-gray-400">{formatDate(inv.due_date)}</td>
                    <td className="px-5 py-3 text-sm text-white text-right font-mono">{formatCurrency(inv.total_fils)}</td>
                    <td className="px-5 py-3 text-sm text-gray-300 text-right font-mono">{formatCurrency(inv.amount_paid_fils)}</td>
                    <td className={`px-5 py-3 text-sm text-right font-mono ${outstanding > 0 && inv.status !== 'cancelled' ? 'text-amber-300' : 'text-gray-500'}`}>
                      {formatCurrency(outstanding)}
                    </td>
                    <td className="px-5 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[inv.status]}`}>{STATUS_LABEL[inv.status]}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {createOpen && (
        <CreateInvoiceModal
          suppliers={suppliers.filter(s => s.active === 1)}
          onClose={() => setCreateOpen(false)}
          onCreated={(id) => { setCreateOpen(false); api.get(`/supplier-invoices/${id}`).then(r => setOpenInvoice(r.data.supplier_invoice)) }}
        />
      )}

      {openInvoice && (
        <InvoiceDrawer
          invoice={openInvoice}
          canManage={canManage}
          onClose={() => setOpenInvoice(null)}
        />
      )}
    </div>
  )
}

const inputCls = 'w-full bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#c41e3a]'
const selectCls = inputCls

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs text-gray-500 uppercase tracking-wide">{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-gray-600 mt-1">{hint}</p>}
    </div>
  )
}

function AgingTile({ label, value, tone, sublabel }: { label: string; value: number; tone: 'neutral' | 'amber' | 'red' | 'bold'; sublabel?: string }) {
  const toneClasses: Record<typeof tone, string> = {
    neutral: 'bg-[#1a1a1a] border-white/10 text-gray-300',
    amber:   'bg-amber-500/10 border-amber-500/30 text-amber-300',
    red:     'bg-red-500/10 border-red-500/30 text-red-300',
    bold:    'bg-[#1a1a1a] border-[#c41e3a] text-white',
  }
  return (
    <div className={`border rounded-xl p-3 ${toneClasses[tone]}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="text-xl font-mono font-bold mt-1">{formatCurrency(value)}</div>
      {sublabel && <div className="text-[10px] opacity-70 mt-0.5">{sublabel}</div>}
    </div>
  )
}

// ── Create invoice ────────────────────────────────────────────────────
function CreateInvoiceModal({ suppliers, onClose, onCreated }: { suppliers: Supplier[]; onClose: () => void; onCreated: (id: string) => void }) {
  const today = new Date().toISOString().slice(0, 10)
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [supplierId, setSupplierId] = useState(suppliers[0]?.id ?? '')
  const [purchaseOrderId, setPurchaseOrderId] = useState('')
  const [invoiceDate, setInvoiceDate] = useState(today)
  const [dueDate, setDueDate] = useState(in30)
  const [subtotalJd, setSubtotalJd] = useState('')
  const [taxJd, setTaxJd] = useState('0')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // POs from this supplier that could be linked. Optional — accountant
  // may prefer to skip the link for non-stock invoices (rent, utilities).
  const { data: posData } = useQuery<{ purchase_orders: PurchaseOrder[] }>({
    queryKey: ['pos-for-supplier', supplierId],
    queryFn: () => api.get(`/purchase-orders?supplier_id=${supplierId}&limit=50`).then(r => r.data),
    enabled: supplierId.length > 0,
  })

  const total = useMemo(() => {
    const s = Math.round(parseFloat(subtotalJd || '0') * 1000)
    const t = Math.round(parseFloat(taxJd || '0') * 1000)
    return s + t
  }, [subtotalJd, taxJd])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (!invoiceNumber.trim()) { setError('Invoice number is required'); return }
    if (!supplierId) { setError('Supplier is required'); return }
    if (!invoiceDate || !dueDate) { setError('Both dates are required'); return }
    if (total <= 0) { setError('Total must be positive'); return }
    setSubmitting(true)
    try {
      const res = await api.post('/supplier-invoices', {
        invoice_number: invoiceNumber.trim(),
        supplier_id: supplierId,
        purchase_order_id: purchaseOrderId || null,
        invoice_date: invoiceDate,
        due_date: dueDate,
        subtotal_fils: Math.round(parseFloat(subtotalJd || '0') * 1000),
        tax_fils: Math.round(parseFloat(taxJd || '0') * 1000),
        notes: notes.trim() || null,
      })
      onCreated(res.data.id as string)
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't create invoice.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={e => e.stopPropagation()}>
      <form onSubmit={submit} className="w-full max-w-lg bg-[#1a1a1a] border border-white/10 rounded-xl p-6 space-y-4 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold">New supplier invoice</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Supplier">
            <select value={supplierId} onChange={e => setSupplierId(e.target.value)} className={selectCls}>
              {suppliers.length === 0 ? <option value="">No active suppliers</option> : suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Supplier invoice #">
            <input type="text" value={invoiceNumber} onChange={e => setInvoiceNumber(e.target.value)} className={inputCls} placeholder="INV-12345" />
          </Field>
        </div>

        <Field label="Link to PO (optional)" hint="Only PO's from this supplier. Skip for rent, utilities, non-stock invoices.">
          <select value={purchaseOrderId} onChange={e => setPurchaseOrderId(e.target.value)} className={selectCls}>
            <option value="">— No PO link —</option>
            {(posData?.purchase_orders ?? []).map(po => <option key={po.id} value={po.id}>{po.po_number} · {po.status}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Invoice date">
            <input type="date" value={invoiceDate} onChange={e => setInvoiceDate(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Due date">
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className={inputCls} />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Subtotal (JD)">
            <input type="number" step="0.001" value={subtotalJd} onChange={e => setSubtotalJd(e.target.value)} className={`${inputCls} font-mono text-right`} placeholder="0.000" />
          </Field>
          <Field label="Tax (JD)" hint="Input VAT — claimable.">
            <input type="number" step="0.001" value={taxJd} onChange={e => setTaxJd(e.target.value)} className={`${inputCls} font-mono text-right`} placeholder="0.000" />
          </Field>
          <Field label="Total (auto)">
            <div className={`${inputCls} font-mono text-right bg-[#1a1a1a] text-white font-semibold`}>
              {formatCurrency(total)}
            </div>
          </Field>
        </div>

        <Field label="Notes (optional)">
          <textarea value={notes} onChange={e => setNotes(e.target.value)} className={`${inputCls} min-h-[60px]`} placeholder="Payment terms, references, etc." />
        </Field>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]">Cancel</button>
          <button type="submit" disabled={submitting} className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50">
            {submitting ? 'Creating…' : 'Create invoice'}
          </button>
        </div>
      </form>
    </div>
  )
}

// ── Invoice drawer ────────────────────────────────────────────────────
function InvoiceDrawer({ invoice, canManage, onClose }: { invoice: Invoice; canManage: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [payOpen, setPayOpen] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)

  const { data, refetch } = useQuery<{ supplier_invoice: Invoice; payments: Payment[] }>({
    queryKey: ['supplier-invoice', invoice.id],
    queryFn: () => api.get(`/supplier-invoices/${invoice.id}`).then(r => r.data),
  })
  const inv = data?.supplier_invoice ?? invoice
  const payments = data?.payments ?? []
  const outstanding = inv.total_fils - inv.amount_paid_fils

  async function cancelInvoice() {
    if (!confirm(`Cancel invoice ${inv.invoice_number}?`)) return
    setBusy('cancel')
    try {
      await api.post(`/supplier-invoices/${inv.id}/cancel`, {})
      await refetch()
      qc.invalidateQueries({ queryKey: ['supplier-invoices'] })
      qc.invalidateQueries({ queryKey: ['supplier-invoices-aging'] })
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      alert(e.response?.data?.message ?? "Couldn't cancel.")
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex justify-end bg-black/60" onClick={onClose}>
      <div className="w-full max-w-2xl h-full bg-[#0f0f0f] border-l border-white/10 overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Supplier invoice</div>
              <div className="text-2xl font-mono font-bold text-white mt-1">{inv.invoice_number}</div>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={22} /></button>
          </div>

          {/* Header card */}
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl p-5 space-y-3">
            <Row label="Supplier" value={inv.supplier_name ?? '—'} />
            {inv.po_number && inv.purchase_order_id && (
              <Row label="Purchase order" value={
                <div className="flex items-center gap-3">
                  <span className="font-mono">{inv.po_number}</span>
                  <button
                    type="button"
                    onClick={() => navigate(`/purchase-orders?open=${inv.purchase_order_id}`)}
                    className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1"
                    title="Open the PO detail drawer"
                  >
                    <ExternalLink size={12} /> Open PO
                  </button>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => navigate(`/purchase-orders?open=${inv.purchase_order_id}&receive=1`)}
                      className="text-xs text-[#c41e3a] hover:text-white inline-flex items-center gap-1 font-semibold"
                      title="Jump to the PO and open the Receive shipment modal"
                    >
                      <PackageCheck size={12} /> Receive shipment
                    </button>
                  )}
                </div>
              } />
            )}
            {inv.po_number && !inv.purchase_order_id && (
              <Row label="Purchase order" value={<span className="font-mono">{inv.po_number}</span>} />
            )}
            <Row label="Status" value={<span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[inv.status]}`}>{STATUS_LABEL[inv.status]}</span>} />
            <Row label="Invoice date" value={formatDate(inv.invoice_date)} />
            <Row label="Due date" value={
              <span className={inv.status === 'overdue' ? 'text-red-400 font-semibold' : ''}>
                {formatDate(inv.due_date)}
                {inv.status === 'overdue' && ' (past due)'}
              </span>
            } />
            <div className="border-t border-white/10 pt-3">
              <Row label="Subtotal" value={<span className="font-mono">{formatCurrency(inv.subtotal_fils)}</span>} />
              <Row label="Tax (input VAT)" value={<span className="font-mono">{formatCurrency(inv.tax_fils)}</span>} />
              <Row label="Total" value={<span className="font-mono font-semibold text-white">{formatCurrency(inv.total_fils)}</span>} />
              <Row label="Paid" value={<span className={`font-mono ${inv.amount_paid_fils > 0 ? 'text-green-400' : ''}`}>{formatCurrency(inv.amount_paid_fils)}</span>} />
              <Row label="Outstanding" value={
                <span className={`font-mono text-lg font-bold ${outstanding > 0 && inv.status !== 'cancelled' ? 'text-amber-300' : 'text-gray-500'}`}>
                  {formatCurrency(outstanding)}
                </span>
              } />
            </div>
            {inv.notes && <Row label="Notes" value={<span className="whitespace-pre-line">{inv.notes}</span>} />}
          </div>

          {/* Payments log */}
          <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <div className="text-sm font-semibold text-white flex items-center gap-2">
                <Wallet size={16} /> Payments ({payments.length})
              </div>
              {canManage && outstanding > 0 && inv.status !== 'cancelled' && (
                <button onClick={() => setPayOpen(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#c41e3a] text-white font-semibold hover:bg-[#a01830]">
                  <Plus size={12} /> Record payment
                </button>
              )}
            </div>
            {payments.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-500">No payments recorded yet.</div>
            ) : (
              <table className="w-full">
                <thead className="bg-white/5 text-[11px] uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2">Date</th>
                    <th className="text-left px-4 py-2">Method</th>
                    <th className="text-left px-4 py-2">Reference</th>
                    <th className="text-right px-4 py-2">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map(p => (
                    <tr key={p.id} className="border-t border-white/10">
                      <td className="px-4 py-2 text-sm text-gray-300">{formatDate(p.payment_date)}</td>
                      <td className="px-4 py-2 text-sm text-white">{METHOD_LABEL[p.method]}</td>
                      <td className="px-4 py-2 text-xs text-gray-400 font-mono">{p.reference ?? <span className="text-gray-600">—</span>}</td>
                      <td className="px-4 py-2 text-sm text-green-400 text-right font-mono">{formatCurrency(p.amount_fils)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Actions */}
          {canManage && ['unpaid', 'overdue'].includes(inv.status) && inv.amount_paid_fils === 0 && (
            <div>
              <button onClick={cancelInvoice} disabled={busy !== null}
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-sm font-semibold hover:bg-red-500/25 disabled:opacity-40">
                {busy === 'cancel' ? <Loader2 size={14} className="animate-spin" /> : <Ban size={14} />}
                Cancel invoice
              </button>
            </div>
          )}

          <div className="text-xs text-gray-600">
            Created {formatDateTime(inv.created_at)}
          </div>
        </div>
      </div>

      {payOpen && (
        <PaymentModal
          invoice={inv}
          onClose={() => setPayOpen(false)}
          onRecorded={() => {
            setPayOpen(false)
            refetch()
            qc.invalidateQueries({ queryKey: ['supplier-invoices'] })
            qc.invalidateQueries({ queryKey: ['supplier-invoices-aging'] })
          }}
        />
      )}
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1">
      <div className="text-xs uppercase tracking-wide text-gray-500 mt-0.5 min-w-[110px]">{label}</div>
      <div className="text-sm text-white text-right flex-1">{value}</div>
    </div>
  )
}

// ── Record payment ────────────────────────────────────────────────────
function PaymentModal({ invoice, onClose, onRecorded }: { invoice: Invoice; onClose: () => void; onRecorded: () => void }) {
  const today = new Date().toISOString().slice(0, 10)
  const outstanding = invoice.total_fils - invoice.amount_paid_fils
  const [paymentDate, setPaymentDate] = useState(today)
  const [amountJd, setAmountJd] = useState((outstanding / 1000).toFixed(3))
  const [method, setMethod] = useState<Payment['method']>('bank_transfer')
  const [reference, setReference] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const amountFils = Math.round(parseFloat(amountJd || '0') * 1000)
    if (amountFils <= 0) { setError('Amount must be positive'); return }
    if (amountFils > outstanding) { setError('Amount exceeds outstanding balance'); return }
    setSubmitting(true)
    try {
      await api.post(`/supplier-invoices/${invoice.id}/payments`, {
        payment_date: paymentDate,
        amount_fils: amountFils,
        method,
        reference: reference.trim() || null,
        notes: notes.trim() || null,
      })
      onRecorded()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? "Couldn't record payment.")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={e => e.stopPropagation()}>
      <form onSubmit={submit} className="w-full max-w-md bg-[#1a1a1a] border border-white/10 rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-base font-semibold flex items-center gap-2">
            <Wallet size={18} className="text-[#c41e3a]" />
            Record payment
          </h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>

        <div className="bg-white/5 border border-white/10 rounded-lg p-3 flex items-center gap-3 text-sm">
          <AlertCircle size={16} className="text-amber-400 shrink-0" />
          <div>
            <div className="text-gray-400">Outstanding balance</div>
            <div className="font-mono font-bold text-white text-lg">{formatCurrency(outstanding)}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Payment date">
            <input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} className={inputCls} />
          </Field>
          <Field label="Amount (JD)">
            <input type="number" step="0.001" value={amountJd} onChange={e => setAmountJd(e.target.value)} className={`${inputCls} font-mono text-right`} />
          </Field>
        </div>

        <Field label="Method">
          <select value={method} onChange={e => setMethod(e.target.value as Payment['method'])} className={selectCls}>
            <option value="bank_transfer">Bank transfer</option>
            <option value="cheque">Cheque</option>
            <option value="cash">Cash</option>
            <option value="card">Card</option>
            <option value="cliq">CliQ</option>
            <option value="other">Other</option>
          </select>
        </Field>

        <Field label="Reference (optional)" hint="Cheque #, bank txn ref, etc.">
          <input type="text" value={reference} onChange={e => setReference(e.target.value)} className={`${inputCls} font-mono`} />
        </Field>

        <Field label="Notes (optional)">
          <textarea value={notes} onChange={e => setNotes(e.target.value)} className={`${inputCls} min-h-[60px]`} />
        </Field>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]">Cancel</button>
          <button type="submit" disabled={submitting} className="flex-1 text-sm py-2 rounded-lg bg-[#c41e3a] text-white font-medium hover:bg-[#a01830] disabled:opacity-50">
            {submitting ? 'Recording…' : 'Record payment'}
          </button>
        </div>
      </form>
    </div>
  )
}
